import React, {useMemo} from "react";
import {AlertCircle, Building, DollarSign, FileText, TrendingDown, TrendingUp} from "lucide-react";
import {Box, Chip, Paper, Stack, Typography} from "@mui/material";
import {buildMonthlyRevenueData, formatDateBR, formatMoney, getFaturaStatusLabel, getFaturaStatusPalette} from "./financeiroUtils";

const StatCard = ({label, value, icon: Icon, iconBg, iconColor, trend, trendUp}) => (
    <Paper
        variant="outlined"
        sx={{
            p: 3,
            borderRadius: 2.5,
            borderColor: '#e5e7eb',
            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
            bgcolor: '#fff',
        }}
    >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{mb: 2}}>
            <Box sx={{width: 48, height: 48, borderRadius: 2, bgcolor: iconBg, color: iconColor, display: 'grid', placeItems: 'center'}}>
                <Icon size={22} />
            </Box>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{color: trendUp ? '#16a34a' : '#dc2626'}}>
                {trendUp ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                <Typography variant="caption" fontWeight={600}>{trend}</Typography>
            </Stack>
        </Stack>
        <Typography sx={{fontSize: {xs: '1.5rem', md: '1.75rem'}, lineHeight: 1.2, color: '#111827', fontWeight: 700, mb: 0.5}}>
            {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Paper>
);

const RevenueBars = ({data}) => {
    const max = Math.max(...data.map((item) => item.receita), 1);

    return (
        <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: {xs: 1, md: 1.5}, alignItems: 'end', height: 300}}>
            {data.map((item) => (
                <Stack key={item.mes} spacing={1} justifyContent="flex-end" sx={{height: '100%'}}>
                    <Box
                        sx={{
                            borderRadius: '10px 10px 4px 4px',
                            background: 'linear-gradient(180deg, #14b8a6 0%, #0f766e 100%)',
                            height: `${Math.max(16, (item.receita / max) * 230)}px`,
                            transition: 'height 0.3s ease',
                        }}
                    />
                    <Typography variant="caption" sx={{textAlign: 'center', color: '#6b7280'}}>
                        {item.mes}
                    </Typography>
                </Stack>
            ))}
        </Box>
    );
};

export default function FinanceiroDashboardPage({dashboard, inadimplentes, loading}) {
    const ultimasFaturas = useMemo(() => dashboard?.ultimas_faturas || [], [dashboard?.ultimas_faturas]);
    const chartData = useMemo(() => buildMonthlyRevenueData(ultimasFaturas), [ultimasFaturas]);

    return (
        <Stack spacing={3}>
            <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)'}, gap: 2}}>
                <StatCard
                    label="Receita Mensal (MRR)"
                    value={formatMoney(dashboard?.mrr)}
                    icon={DollarSign}
                    iconBg="#ccfbf1"
                    iconColor="#0f766e"
                    trend="+8%"
                    trendUp
                />
                <StatCard
                    label="Faturas em Aberto"
                    value={dashboard?.quantidade_faturas_abertas || 0}
                    icon={FileText}
                    iconBg="#fef3c7"
                    iconColor="#b45309"
                    trend="-3%"
                    trendUp={false}
                />
                <StatCard
                    label="Faturas Vencidas"
                    value={dashboard?.quantidade_faturas_vencidas || 0}
                    icon={AlertCircle}
                    iconBg="#fee2e2"
                    iconColor="#b91c1c"
                    trend="+5%"
                    trendUp
                />
                <StatCard
                    label="Empresas Ativas"
                    value={dashboard?.empresas_ativas || 0}
                    icon={Building}
                    iconBg="#dcfce7"
                    iconColor="#15803d"
                    trend="+12%"
                    trendUp
                />
            </Box>

            <Paper
                variant="outlined"
                sx={{
                    p: {xs: 2.5, md: 3},
                    borderRadius: 2.5,
                    borderColor: '#e5e7eb',
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                }}
            >
                <Typography sx={{fontSize: '1.05rem', color: '#111827', fontWeight: 600, mb: 3}}>
                    Receita por Mês
                </Typography>
                <RevenueBars data={chartData} />
            </Paper>

            <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', lg: '1fr 1fr'}, gap: 2}}>
                <Paper variant="outlined" sx={{borderRadius: 2.5, overflow: 'hidden', borderColor: '#e5e7eb'}}>
                    <Box sx={{px: 3, py: 2, borderBottom: '1px solid #e5e7eb'}}>
                        <Typography sx={{fontSize: '1rem', color: '#111827', fontWeight: 600}}>
                            Últimas Faturas Geradas
                        </Typography>
                    </Box>
                    <Stack divider={<Box sx={{borderTop: '1px solid #e5e7eb'}} />}>
                        {!ultimasFaturas.length ? (
                    <Box sx={{p: 3}}>
                        <Typography color="text.secondary">{loading ? 'Carregando faturas...' : 'Nenhuma fatura gerada.'}</Typography>
                    </Box>
                ) : ultimasFaturas.map((fatura) => {
                            const palette = getFaturaStatusPalette(fatura.status);
                            return (
                                <Box key={fatura.id} sx={{p: 2.25}}>
                                    <Stack direction="row" justifyContent="space-between" spacing={2} sx={{mb: 1}}>
                                        <Box>
                                            <Typography sx={{color: '#111827', fontWeight: 500}}>{fatura.empresa?.nome || '-'}</Typography>
                                            <Typography variant="body2" color="text.secondary">Competência: {fatura.competencia}</Typography>
                                        </Box>
                                        <Chip
                                            label={getFaturaStatusLabel(fatura.status)}
                                            size="small"
                                            sx={{bgcolor: palette.bg, color: palette.color, border: `1px solid ${palette.border}`}}
                                        />
                                    </Stack>
                                    <Stack direction="row" justifyContent="space-between">
                                        <Typography variant="body2" color="text.secondary">
                                            Vence em {formatDateBR(fatura.vencimento)}
                                        </Typography>
                                        <Typography fontWeight={600}>{formatMoney(fatura.valor_total)}</Typography>
                                    </Stack>
                                </Box>
                            );
                        })}
                    </Stack>
                </Paper>

                <Paper variant="outlined" sx={{borderRadius: 2.5, overflow: 'hidden', borderColor: '#e5e7eb'}}>
                    <Box sx={{px: 3, py: 2, borderBottom: '1px solid #fecaca', bgcolor: '#fef2f2'}}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <AlertCircle size={18} color="#b91c1c" />
                            <Typography sx={{fontSize: '1rem', color: '#111827', fontWeight: 600}}>
                                Empresas Inadimplentes
                            </Typography>
                        </Stack>
                    </Box>
                    <Stack divider={<Box sx={{borderTop: '1px solid #e5e7eb'}} />}>
                        {!inadimplentes.length ? (
                            <Box sx={{p: 3}}>
                                <Typography color="text.secondary">{loading ? 'Carregando inadimplência...' : 'Nenhuma empresa inadimplente.'}</Typography>
                            </Box>
                        ) : inadimplentes.slice(0, 5).map((item) => (
                            <Box key={item.id} sx={{p: 2.25}}>
                                <Stack direction="row" justifyContent="space-between" spacing={2} sx={{mb: 1}}>
                                    <Box>
                                        <Typography sx={{color: '#111827', fontWeight: 500}}>{item.empresa?.nome || '-'}</Typography>
                                        <Typography variant="body2" color="text.secondary">Competência: {item.competencia}</Typography>
                                    </Box>
                                    <Chip
                                        label={`${item.diasAtraso} dias`}
                                        size="small"
                                        sx={{bgcolor: '#fee2e2', color: '#b91c1c', border: '1px solid #fecaca'}}
                                    />
                                </Stack>
                                <Stack direction="row" justifyContent="space-between">
                                    <Typography variant="body2" sx={{color: '#b91c1c'}}>
                                        Venceu em {formatDateBR(item.vencimento)}
                                    </Typography>
                                    <Typography fontWeight={600}>{formatMoney(item.valor_total)}</Typography>
                                </Stack>
                            </Box>
                        ))}
                    </Stack>
                </Paper>
            </Box>

            <Paper
                variant="outlined"
                sx={{
                    p: {xs: 2.5, md: 3},
                    borderRadius: 2.5,
                    borderColor: '#e5e7eb',
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                }}
            >
                <Typography sx={{fontSize: '1rem', color: '#111827', fontWeight: 600}}>Resumo Financeiro</Typography>
                <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', md: 'repeat(3, 1fr)'}, gap: 2, mt: 2}}>
                    <Box>
                        <Typography variant="body2" color="text.secondary">Receita Confirmada (MRR)</Typography>
                        <Typography variant="h5" fontWeight={700} sx={{color: '#15803d'}}>{formatMoney(dashboard?.mrr)}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary">Faturas em Aberto</Typography>
                        <Typography variant="h5" fontWeight={700} sx={{color: '#b45309'}}>{dashboard?.quantidade_faturas_abertas || 0}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary">Empresas Inadimplentes</Typography>
                        <Typography variant="h5" fontWeight={700} sx={{color: '#0f766e'}}>{inadimplentes.length}</Typography>
                    </Box>
                </Box>
            </Paper>
        </Stack>
    );
}
